import { useState, useRef, useCallback } from "react";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { X, Check, RotateCw, Maximize, ZoomIn, ZoomOut } from "lucide-react";

interface PhotoCropModalProps {
  isOpen: boolean;
  imageSrc: string;
  onClose: () => void;
  onCropComplete: (croppedBlob: Blob) => void;
  aspectRatio?: number;
}

function centerAspectCrop(
  mediaWidth: number,
  mediaHeight: number,
  aspect: number
) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: "%",
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

const PhotoCropModal = ({
  isOpen,
  imageSrc,
  onClose,
  onCropComplete,
  aspectRatio = 3 / 4,
}: PhotoCropModalProps) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);

  const onImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const { naturalWidth, naturalHeight } = e.currentTarget;
      const initialCrop = centerAspectCrop(naturalWidth, naturalHeight, aspectRatio);
      setCrop(initialCrop);
    },
    [aspectRatio]
  );

  const handleRotate = useCallback(() => {
    setRotation((prev) => (prev + 90) % 360);
  }, []);

  const handleZoomIn = useCallback(() => {
    setScale((prev) => Math.min(prev + 0.1, 3));
  }, []);

  const handleZoomOut = useCallback(() => {
    setScale((prev) => Math.max(prev - 0.1, 0.5));
  }, []);

  const handleCenterCrop = useCallback(() => {
    if (imgRef.current) {
      const { naturalWidth, naturalHeight } = imgRef.current;
      setCrop(centerAspectCrop(naturalWidth, naturalHeight, aspectRatio));
    }
  }, [aspectRatio]);

  const getCroppedBlob = useCallback(async (): Promise<Blob | null> => {
    if (!imgRef.current || !completedCrop) return null;

    const image = imgRef.current;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    // Set canvas size to the cropped area
    const cropWidth = completedCrop.width * scaleX;
    const cropHeight = completedCrop.height * scaleY;

    // For rotation, we need to adjust canvas size
    const rotationRad = (rotation * Math.PI) / 180;
    const sin = Math.abs(Math.sin(rotationRad));
    const cos = Math.abs(Math.cos(rotationRad));

    if (rotation === 0 || rotation === 180) {
      canvas.width = cropWidth;
      canvas.height = cropHeight;
    } else {
      canvas.width = cropHeight;
      canvas.height = cropWidth;
    }

    // Apply transformations
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate(rotationRad);
    ctx.scale(scale, scale);
    ctx.translate(-canvas.width / 2, -canvas.height / 2);

    // Draw the cropped image
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      cropWidth,
      cropHeight,
      (canvas.width - cropWidth * scale) / 2,
      (canvas.height - cropHeight * scale) / 2,
      cropWidth * scale,
      cropHeight * scale
    );

    return new Promise((resolve) => {
      canvas.toBlob(
        (blob) => resolve(blob),
        "image/jpeg",
        0.92
      );
    });
  }, [completedCrop, rotation, scale]);

  const handleConfirm = useCallback(async () => {
    const blob = await getCroppedBlob();
    if (blob) {
      onCropComplete(blob);
    }
  }, [getCroppedBlob, onCropComplete]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-black/80">
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X className="w-6 h-6 text-white" />
        </button>

        <h2 className="text-white font-medium">Crop Photo</h2>

        <button
          onClick={handleConfirm}
          disabled={!completedCrop}
          className="p-2 rounded-full bg-pop hover:bg-pop/80 transition-colors disabled:opacity-50"
        >
          <Check className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Crop Area */}
      <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
        <ReactCrop
          crop={crop}
          onChange={(c) => setCrop(c)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspectRatio}
          className="max-h-full"
        >
          <img
            ref={imgRef}
            src={imageSrc}
            alt="Crop preview"
            onLoad={onImageLoad}
            style={{
              transform: `rotate(${rotation}deg) scale(${scale})`,
              maxHeight: "60vh",
              maxWidth: "100%",
            }}
            className="transition-transform duration-200"
          />
        </ReactCrop>
      </div>

      {/* Controls */}
      <div className="p-4 bg-black/80">
        <div className="flex items-center justify-center gap-4">
          <button
            onClick={handleZoomOut}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            title="Zoom out"
          >
            <ZoomOut className="w-5 h-5 text-white" />
          </button>

          <button
            onClick={handleRotate}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            title="Rotate"
          >
            <RotateCw className="w-5 h-5 text-white" />
          </button>

          <button
            onClick={handleCenterCrop}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            title="Center crop"
          >
            <Maximize className="w-5 h-5 text-white" />
          </button>

          <button
            onClick={handleZoomIn}
            className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            title="Zoom in"
          >
            <ZoomIn className="w-5 h-5 text-white" />
          </button>
        </div>

        <p className="text-center text-white/60 text-xs mt-3">
          Drag to reposition • Pinch corners to resize
        </p>
      </div>
    </div>
  );
};

export default PhotoCropModal;
